/** Response format utilities - handle standard API response format. */

import type { ApiResponse, SuccessResponse, ErrorResponse, PaginatedResponse, ListResponse, ErrorDetail, PaginationMeta } from "@/lib/api/types";
import type { APIError } from "@/types/api";

/** Narrow unknown JSON object bodies from generic API success payloads. */
export function apiPayloadRecord(data: unknown): Record<string, unknown> {
  if (data !== null && typeof data === "object" && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  return {};
}

/**
 * Check if response is success response.
 *
 * @param response - API response
 * @returns True if success response
 */
export function isSuccessResponse<T>(response: SuccessResponse<T> | ErrorResponse): response is SuccessResponse<T> {
  return "success" in response && response.success === true;
}

/**
 * Check if response is error response.
 *
 * @param response - API response
 * @returns True if error response
 */
export function isErrorResponse(response: SuccessResponse<unknown> | ErrorResponse): response is ErrorResponse {
  return "success" in response && response.success === false;
}

export const ASSIGNEE_WORK_TIMER_CONFLICT_CODE = "assignee_work_timer_already_active";
export const PROJECT_CUSTOMER_ID_MISSING_REASON = "missing_party_id";
export const PROJECT_CUSTOMER_ID_UNKNOWN_REASON = "unknown_party_id";

/** FastAPI often returns `{ detail: string }` or `{ detail: { message, code, ... } }`. */
export function messageFromFastApiErrorBody(data: unknown): string {
  if (!data || typeof data !== "object") return "";
  const detail = (data as { detail?: unknown }).detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => (typeof item === "object" && item && "msg" in item ? String((item as { msg?: unknown }).msg ?? "") : String(item)))
      .filter(Boolean)
      .join("; ");
  }
  if (detail && typeof detail === "object" && "message" in detail && typeof (detail as { message?: unknown }).message === "string") {
    return (detail as { message: string }).message;
  }
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const validationErrors = (detail as { validation_errors?: unknown }).validation_errors;
    if (validationErrors && typeof validationErrors === "object" && !Array.isArray(validationErrors)) {
      const parts: string[] = [];
      for (const value of Object.values(validationErrors as Record<string, unknown>)) {
        if (typeof value === "string" && value.trim()) {
          parts.push(value.trim());
          continue;
        }
        if (Array.isArray(value)) {
          for (const entry of value) {
            if (typeof entry === "string" && entry.trim()) parts.push(entry.trim());
          }
        }
      }
      if (parts.length > 0) return parts.join("; ");
    }
  }
  return "";
}

/** Hub record-action errors embed `code`, `reason`, `record_id` on FastAPI `detail`. */
export function hubDetailFieldsFromFastApiBody(data: unknown): Record<string, string> {
  if (!data || typeof data !== "object") return {};
  const detail = (data as { detail?: unknown }).detail;
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) return {};
  const d = detail as Record<string, unknown>;
  const out: Record<string, string> = {};
  if (typeof d.code === "string" && d.code.trim()) out.hub_code = d.code.trim();
  if (typeof d.http_class === "string" && d.http_class.trim()) out.http_class = d.http_class.trim();
  if (typeof d.reason_code === "string" && d.reason_code.trim()) out.hub_reason_code = d.reason_code.trim();
  if (typeof d.reason === "string" && d.reason.trim()) out.hub_reason = d.reason.trim();
  if (typeof d.field === "string" && d.field.trim()) out.hub_field = d.field.trim();
  if (typeof d.message === "string" && d.message.trim()) out.hub_message = d.message.trim();
  if (typeof d.party_id === "string" && d.party_id.trim()) out.hub_party_id = d.party_id.trim();
  if (typeof d.record_id === "string" && d.record_id.trim()) out.hub_record_id = d.record_id.trim();
  if (typeof d.active_task_id === "string" && d.active_task_id.trim()) out.hub_active_task_id = d.active_task_id.trim();
  if (!out.hub_code && out.hub_reason_code) {
    out.hub_code = out.hub_reason_code;
  }
  const nested = d.details;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    const sr = (nested as Record<string, unknown>).skip_reason;
    if (typeof sr === "string" && sr.trim()) out.hub_skip_reason = sr.trim();
  }
  return out;
}

export function hubDetailFieldsFromEnvelopeBody(data: unknown): Record<string, string> {
  if (!data || typeof data !== "object" || !("success" in data) || (data as { success?: unknown }).success !== false) {
    return {};
  }
  const err = (data as { error?: unknown }).error;
  if (!err || typeof err !== "object" || Array.isArray(err)) return {};
  const e = err as Record<string, unknown>;
  const out: Record<string, string> = {};
  const code = typeof e.code === "string" ? e.code.trim() : "";
  if (code && code !== "HTTP_ERROR") out.hub_code = code;
  const details = e.details;
  if (details && typeof details === "object" && !Array.isArray(details)) {
    const d = details as Record<string, unknown>;
    if (typeof d.reason === "string" && d.reason.trim()) out.hub_reason = d.reason.trim();
    if (typeof d.reason_code === "string" && d.reason_code.trim()) out.hub_reason_code = d.reason_code.trim();
    if (typeof d.message === "string" && d.message.trim()) out.hub_message = d.message.trim();
    if (typeof d.party_id === "string" && d.party_id.trim()) out.hub_party_id = d.party_id.trim();
    if (typeof d.record_id === "string" && d.record_id.trim()) out.hub_record_id = d.record_id.trim();
    if (typeof d.active_task_id === "string" && d.active_task_id.trim()) out.hub_active_task_id = d.active_task_id.trim();
    if (!out.hub_code && out.hub_reason_code) {
      out.hub_code = out.hub_reason_code;
    }
    const nested = d.details;
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      const sr = (nested as Record<string, unknown>).skip_reason;
      if (typeof sr === "string" && sr.trim()) out.hub_skip_reason = sr.trim();
    }
  }
  return out;
}

export function hubDetailFieldsFromErrorBody(data: unknown): Record<string, string> {
  const fastApi = hubDetailFieldsFromFastApiBody(data);
  if (Object.keys(fastApi).length > 0) return fastApi;
  return hubDetailFieldsFromEnvelopeBody(data);
}

function envelopeErrorDetail(body: unknown): ErrorDetail | null {
  if (!body || typeof body !== "object" || !("success" in body) || (body as { success?: unknown }).success !== false) {
    return null;
  }
  const err = (body as { error?: unknown }).error;
  if (!err || typeof err !== "object" || Array.isArray(err)) return null;
  const e = err as Record<string, unknown>;
  const code = typeof e.code === "string" && e.code.trim() ? e.code.trim() : "HTTP_ERROR";
  const message = typeof e.message === "string" && e.message.trim() ? e.message.trim() : "";
  if (!message) return null;
  const details =
    e.details && typeof e.details === "object" && !Array.isArray(e.details)
      ? { ...(e.details as Record<string, unknown>) }
      : {};
  return { code, message, details };
}

/** Normalize Gateway HTTP error JSON into `ErrorDetail` for fetch and axios clients. */
export function gatewayHttpFailureFromBody(status: number, statusText: string, body: unknown): ErrorDetail {
  const envelope = envelopeErrorDetail(body);
  if (envelope) {
    const hubFields = hubDetailFieldsFromEnvelopeBody(body);
    return {
      code: envelope.code,
      message: envelope.message,
      details: {
        status,
        statusText,
        ...envelope.details,
        ...hubFields,
      },
    };
  }

  const fastApiMessage = messageFromFastApiErrorBody(body);
  const hubFields = hubDetailFieldsFromErrorBody(body);
  let message = fastApiMessage.trim();
  if (!message && statusText.trim()) {
    message = statusText.trim();
  }
  if (!message) {
    message = "Request failed";
  }

  let code = "HTTP_ERROR";
  if (hubFields.hub_code) {
    code = hubFields.hub_code;
  }

  const details: Record<string, unknown> = {
    status,
    statusText,
    ...hubFields,
  };
  if (body && typeof body === "object" && !Array.isArray(body)) {
    const detail = (body as { detail?: unknown }).detail;
    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
      const blockers = (detail as { blockers?: unknown }).blockers;
      if (Array.isArray(blockers)) {
        details.blockers = blockers;
      }
    }
  }

  return {
    code,
    message,
    details,
  };
}

/** User-facing text from a gateway error string or structured `APIError`. */
export function apiResponseErrorMessage(error: string | APIError | undefined): string {
  if (error === undefined) {
    return "Request failed";
  }
  if (typeof error === "string") {
    const trimmed = error.trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
    return "Request failed";
  }
  const message = error.message.trim();
  if (message.length > 0) {
    return message;
  }
  const code = error.code.trim();
  if (code.length > 0) {
    return code;
  }
  return "Request failed";
}

export function hubClientErrorMessage(response: ApiResponse<unknown>, fallback = "Request failed"): string {
  if (response.success !== false) {
    return fallback;
  }
  const failed = response as ErrorResponse;
  const message = failed.error.message.trim();
  if (message.length > 0) {
    return message;
  }
  const code = failed.error.code.trim();
  if (code.length > 0) {
    return code;
  }
  return fallback;
}

/** User-facing text from gateway `APIResponse` failure fields. */
export function gatewayResponseErrorMessage(response: {
  error?: string;
  errorDetail?: APIError;
  message?: string;
}): string {
  if (response.error !== undefined) {
    const trimmed = response.error.trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
  }
  if (response.errorDetail !== undefined) {
    return apiResponseErrorMessage(response.errorDetail);
  }
  if (response.message !== undefined) {
    const trimmed = response.message.trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
  }
  return "Request failed";
}

function errorDetailsRecord(res: ApiResponse<unknown>): Record<string, unknown> {
  if (!isErrorResponse(res)) return {};
  const d = res.error.details;
  if (d && typeof d === "object" && !Array.isArray(d)) return d as Record<string, unknown>;
  return {};
}

/** Map envelope `error.code` / `error.details` and FastAPI `hub_*` fields to one detail record. */
export function normalizedHubErrorDetails(res: ApiResponse<unknown>): Record<string, unknown> {
  if (!isErrorResponse(res)) return {};
  const raw = errorDetailsRecord(res);
  const out: Record<string, unknown> = { ...raw };
  const errCode = String(res.error.code || "").trim();
  if (!out.hub_code && errCode && errCode !== "HTTP_ERROR") {
    out.hub_code = errCode;
  }
  if (!out.hub_reason && typeof raw.reason === "string" && String(raw.reason).trim()) {
    out.hub_reason = String(raw.reason).trim();
  }
  if (!out.hub_message && typeof raw.message === "string" && String(raw.message).trim()) {
    out.hub_message = String(raw.message).trim();
  }
  if (!out.hub_party_id && typeof raw.party_id === "string" && String(raw.party_id).trim()) {
    out.hub_party_id = String(raw.party_id).trim();
  }
  if (!out.hub_record_id && typeof raw.record_id === "string" && String(raw.record_id).trim()) {
    out.hub_record_id = String(raw.record_id).trim();
  }
  if (!out.hub_active_task_id && typeof raw.active_task_id === "string" && String(raw.active_task_id).trim()) {
    out.hub_active_task_id = String(raw.active_task_id).trim();
  }
  return out;
}

/** User-facing message when project/approve party_id binding fails. */
export function projectPartyIdInvalidMessage(details: Record<string, unknown> | undefined): string | null {
  const code = String(details?.hub_code || "").trim();
  const reason = String(details?.hub_reason || "").trim();
  const rawPartyId = String(details?.hub_party_id || "").trim();
  if (reason === PROJECT_CUSTOMER_ID_MISSING_REASON || code === PROJECT_CUSTOMER_ID_MISSING_REASON) {
    return "Customer is required. Select a customer from the Customer dropdown on Project or Approve.";
  }
  if (reason === PROJECT_CUSTOMER_ID_UNKNOWN_REASON || code === PROJECT_CUSTOMER_ID_UNKNOWN_REASON) {
    if (rawPartyId) {
      return (
        `Unknown customer id "${rawPartyId}": party_id must reference an existing Customer. ` +
        "Select a customer from the Customer dropdown."
      );
    }
    return "Unknown party_id: party_id must reference an existing Customer. Select a customer from the Customer dropdown.";
  }
  return null;
}

function workTimerConflictTitleFromMessage(message: string): string {
  const match = message.match(/running on "([^"]+)"/i);
  return match?.[1]?.trim() || "";
}

/** Strip legacy suffixes when errors were thrown as plain `Error` strings. */
export function stripAssigneeWorkTimerConflictSuffix(message: string): string {
  return message
    .replace(/\s*\(assignee_work_timer_already_active\)\s*$/i, "")
    .replace(/\s*\[assignee_work_timer_already_active\]\s*$/i, "")
    .trim();
}

export function assigneeWorkTimerConflictFromCaught(
  e: unknown
): { message: string; activeTaskId: string; peerTitle: string } | null {
  if (e !== null && typeof e === "object" && "success" in e && (e as ApiResponse<unknown>).success === false) {
    const o = normalizedHubErrorDetails(e as ApiResponse<unknown>);
    const code = String(o.hub_code || "").trim();
    if (code !== ASSIGNEE_WORK_TIMER_CONFLICT_CODE) return null;
    const activeTaskId = String(o.hub_active_task_id || "").trim();
    const message = stripAssigneeWorkTimerConflictSuffix(apiFailureMessage(e as ApiResponse<unknown>));
    const peerTitle = workTimerConflictTitleFromMessage(message) || activeTaskId;
    return { message, activeTaskId, peerTitle };
  }
  if (e instanceof Error) {
    const message = stripAssigneeWorkTimerConflictSuffix(e.message);
    if (!/work timer/i.test(message)) return null;
    const peerTitle = workTimerConflictTitleFromMessage(message);
    return { message, activeTaskId: "", peerTitle };
  }
  return null;
}

export function apiFailureMessage(res: ApiResponse<unknown>): string {
  if (!isErrorResponse(res)) return "Request failed.";
  const o = normalizedHubErrorDetails(res);
  const projectCustomerMsg = projectPartyIdInvalidMessage(o);
  if (projectCustomerMsg) return projectCustomerMsg;
  const rawMessage = typeof res.error.message === "string" ? res.error.message.trim() : "";
  let msg = rawMessage.length > 0 ? rawMessage : "Request failed.";
  const d = res.error.details;
  if (d && typeof d === "object" && !Array.isArray(d)) {
    const hubCode = typeof o.hub_code === "string" ? o.hub_code.trim() : "";
    const reason = o.hub_reason;
    const customerBinding =
      hubCode === PROJECT_CUSTOMER_ID_MISSING_REASON ||
      hubCode === PROJECT_CUSTOMER_ID_UNKNOWN_REASON ||
      reason === PROJECT_CUSTOMER_ID_MISSING_REASON ||
      reason === PROJECT_CUSTOMER_ID_UNKNOWN_REASON;
    if (
      typeof reason === "string" &&
      reason.trim() &&
      hubCode !== ASSIGNEE_WORK_TIMER_CONFLICT_CODE &&
      !customerBinding
    ) {
      msg = `${msg} (${reason.trim()})`;
    }
    const skipReason = o.hub_skip_reason;
    if (typeof skipReason === "string" && skipReason.trim()) {
      const s = skipReason.trim();
      if (!msg.includes(s)) {
        msg = `${msg} — ${s}`;
      }
    }
    const code = o.hub_code;
    if (
      typeof code === "string" &&
      code.trim() &&
      code.trim() !== ASSIGNEE_WORK_TIMER_CONFLICT_CODE &&
      !customerBinding
    ) {
      const c = code.trim();
      if (!msg.includes(c)) {
        msg = `${msg} [${c}]`;
      }
    }
  }
  return msg;
}

/**
 * Convert success response to data.
 *
 * @param response - Success response
 * @returns Response data
 */
export function dataFromResponse<T>(response: SuccessResponse<T>): T {
  return response.data;
}

/**
 * Convert paginated response to items array.
 *
 * @param response - Paginated response
 * @returns Items array
 */
export function itemsFromPaginated<T>(response: PaginatedResponse<T>): T[] {
  return response.data.items;
}

/**
 * Convert list response to items array.
 *
 * @param response - List response
 * @returns Items array
 */
export function itemsFromList<T>(response: ListResponse<T>): T[] {
  return response.data.items;
}

/**
 * Convert paginated response to pagination metadata.
 *
 * @param response - Paginated response
 * @returns Pagination metadata
 */
export function paginationFromResponse<T>(response: PaginatedResponse<T>) {
  return response.data.pagination;
}

/** Narrow `pagination` from Intake Core list payloads (`data` or raw body) where shape may vary. */
export function parsePaginationMeta(data: unknown): PaginationMeta | undefined {
  if (!data || typeof data !== "object" || Array.isArray(data)) return undefined;
  const p = (data as Record<string, unknown>).pagination;
  if (!p || typeof p !== "object" || Array.isArray(p)) return undefined;
  const o = p as Record<string, unknown>;
  if (typeof o.page_size !== "number") return undefined;
  return p as PaginationMeta;
}

export function totalPagesFromOffsetPayload(total: number, pageSize: number, pagination: PaginationMeta | undefined): number {
  if (pagination?.total_pages != null) return Math.max(1, pagination.total_pages);
  return Math.max(1, Math.ceil(total / pageSize) || 1);
}

/**
 * Extract error detail from error response.
 *
 * @param response - Error response
 * @returns Error detail object
 */
export function errorFromResponse(response: ErrorResponse): ErrorDetail {
  return response.error;
}

/**
 * Check if error is retryable.
 *
 * @param error - Error detail
 * @returns True if error is retryable
 */
export function isRetryableError(error: ErrorDetail): boolean {
  // Don't retry client errors (4xx) except 408 (Request Timeout) and 429 (Too Many Requests)
  if (error.details && "status" in error.details) {
    const status = error.details.status as number;
    if (status >= 400 && status < 500) {
      return status === 408 || status === 429;
    }
  }
  // Retry server errors (5xx) and network errors
  if (error.code === "NETWORK_ERROR") {
    return true;
  }
  if (error.details && "status" in error.details && (error.details.status as number) >= 500) {
    return true;
  }
  return false;
}

/**
 * Message for errors thrown from the API client (e.g. axios reject with normalized ErrorResponse body).
 */
export class ListPayloadError extends Error {
  readonly i18nKey: string;

  constructor(technicalDetail: string, i18nKey = "common.invalidListPayload") {
    super(technicalDetail);
    this.name = "ListPayloadError";
    this.i18nKey = i18nKey;
  }
}

export function userMessageFromCaughtError(error: unknown, t: (key: string) => string): string {
  if (error instanceof ListPayloadError) {
    console.error(error.message);
    return t(error.i18nKey);
  }
  return messageFromCaughtApiError(error);
}

export function messageFromCaughtApiError(e: unknown): string {
  const conflict = assigneeWorkTimerConflictFromCaught(e);
  if (conflict) return conflict.message;
  if (e instanceof Error) return stripAssigneeWorkTimerConflictSuffix(e.message);
  let message = "Request failed.";
  if (e !== null && typeof e === "object" && "success" in e && (e as ApiResponse<unknown>).success === false) {
    message = apiFailureMessage(e as ApiResponse<unknown>);
  }
  const requestId =
    e !== null &&
    typeof e === "object" &&
    "meta" in e &&
    typeof (e as { meta?: { request_id?: unknown } }).meta?.request_id === "string"
      ? String((e as { meta: { request_id: string } }).meta.request_id).trim()
      : "";
  if (requestId && requestId !== "unknown" && !message.includes(requestId)) {
    return `${message} [requestId: ${requestId}]`;
  }
  return message;
}

/**
 * Get user-friendly error message from error detail.
 *
 * @param error - Error detail
 * @returns User-friendly error message
 */
export function userFriendlyMessage(error: ErrorDetail): string {
  return error.message || error.code || "An unknown error occurred";
}

// Legacy exports for backward compatibility (deprecated)
/** @deprecated Use dataFromResponse instead */
export const extractData = dataFromResponse;
/** @deprecated Use itemsFromPaginated instead */
export const extractItems = itemsFromPaginated;
/** @deprecated Use itemsFromList instead */
export const extractListItems = itemsFromList;
/** @deprecated Use paginationFromResponse instead */
export const extractPagination = paginationFromResponse;
